import { useLayoutEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import "./Listing.scss";
import "src/app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchForm.scss";
import ListingHeaderContainer from "app/pages/Listing/ListingHeader/ListingHeaderContainer";
import {
	PRODUCT_EXPERIENCES,
	PRODUCTS_FILTERS_KEY,
	PRODUCT_TAG_CODES,
	MULTI_STAGE_JOURNEY_OPTIONS,
	OFFER_TYPE_LABELS,
} from "app/constants";
import { scrollToElement } from "app/utils/scroller";
import { forceCheck } from "react-lazyload";
import ListingContentContainer from "app/pages/Listing/ListingContent/ListingContentContainer";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import ListingHeadMetadata from "app/pages/Listing/ListingHeadMetadata";
import ScrollToTopButton from "app/pages/.shared/ScrollToTopButton/ScrollToTopButton";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { useIntl } from "react-intl";
import { FilterProvider, OPTION_MODE } from "app/features/product/productFilters";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { ActionBar } from "app/features/product/productActionBar/ActionBar";
import { SortProvider } from "app/features/product/productSort";
import { sendTagOnProductsFilterOpen } from "app/utils/analytics";

const CUSTOM_COMPARATORS = {
	recommendation: (a, b) => (a?.rank || 0) - (b?.rank || 0),
	price_asc: (a, b) => (a?.fromPriceType?.value ?? 0) - (b?.fromPriceType?.value ?? 0),
	price_desc: (a, b) => (b?.fromPriceType?.value ?? 0) - (a?.fromPriceType?.value ?? 0),
};

const Listing = ({ idSpotlight, displayProductsCount, listingSEO = {}, resolution, products }) => {
	const { track } = useAnalytics();
	const listingHeaderRef = useRef();
	const topOfProductListRef = useRef();
	const { formatDate, formatMessage } = useIntl();
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	useLayoutEffect(() => {
		// force l'affichage des images des produits sur mobile lorsqu'un filtre est actif
		forceCheck();
	}, []);

	const scrollToListingHeader = () => {
		scrollToElement({
			element: listingHeaderRef.current,
			offset: 0,
			disableSmoothScroll: true,
		});
	};

	const shouldShowMultiStageJourneyOption = products?.some(product =>
		[
			PRODUCT_EXPERIENCES.CIRCUIT,
			PRODUCT_EXPERIENCES.AUTOTOUR,
			PRODUCT_EXPERIENCES.CRUISE,
		].includes(product?.productExperience)
	);

	const facetConfigs = useMemo(
		() => [
			{
				facetId: PRODUCTS_FILTERS_KEY.DESTINATION,
				facetTitle: formatMessage({ id: "product.page.filter.destinations.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.DESTINATION,
						title: formatMessage({ id: "product.page.filter.destinations.label" }),
						valueGetter: item => item.country,

						...(shouldShowMultiStageJourneyOption && {
							buildOptions: () => [
								{
									value: "multi-stage-journey",
									label: formatMessage({
										id: "listing.filter.destination.multi.destinations.label",
									}),
								},
							],
							customMatcher: product =>
								[
									PRODUCT_EXPERIENCES.CIRCUIT,
									PRODUCT_EXPERIENCES.AUTOTOUR,
									PRODUCT_EXPERIENCES.CRUISE,
								].includes(product?.productExperience),
						}),

						allowSearch: true,
						search: { placeholderKey: "filter.destination.searchbar.placeholder" },
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
				facetTitle: formatMessage({ id: "product.page.filter.offer.type.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
						title: formatMessage({ id: "product.page.filter.offer.type.label" }),
						valueGetter: product => product.merchandisings,
						buildOptions: products => {
							const offerTypes = [
								...new Set(
									products
										.flatMap(product => product.merchandisings)
										.map(merchandising => {
											if (
												merchandising?.startsWith(
													PRODUCT_TAG_CODES.COUP_DE_COEUR
												)
											) {
												return PRODUCT_TAG_CODES.COUP_DE_COEUR;
											}
											if (merchandising?.startsWith(PRODUCT_TAG_CODES.GC)) {
												return PRODUCT_TAG_CODES.GC;
											}
											return null;
										})
										.filter(Boolean)
								),
							];

							return offerTypes.map(merchandising => ({
								value: merchandising,
								labelKey: OFFER_TYPE_LABELS[merchandising] ?? merchandising,
							}));
						},
						customMatcher: (product, selectedValues) => {
							const productOfferTypes = product.merchandisings;

							return productOfferTypes?.some(offerType =>
								selectedValues?.some(selectedValue =>
									offerType.startsWith(selectedValue)
								)
							);
						},
						allowSingleOption: true,
						optionMode: OPTION_MODE.MANUAL_ONLY,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
				facetTitle: formatMessage({ id: "multi.stage.journey" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
						title: formatMessage({ id: "multi.stage.journey" }),
						valueGetter: product => product.productExperience,
						buildOptions: products =>
							[...new Set(products.map(product => product.productExperience))]
								.filter(option =>
									Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(option)
								)
								.map(option => ({
									value: option,
									label: formatMessage({
										id: MULTI_STAGE_JOURNEY_OPTIONS[option],
									}),
								})),
						optionMode: OPTION_MODE.MANUAL_ONLY,
						allowSingleOption: true,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.TOPIC,
				facetTitle: formatMessage({ id: "listing.filter.theme.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.TOPIC,
						title: formatMessage({ id: "listing.filter.theme.label" }),
						valueGetter: item => item.topics,
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.MONTH,
				facetTitle: formatMessage({ id: "listing.filter.month.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.MONTH,
						title: formatMessage({ id: "listing.filter.month.label" }),
						valueGetter: item => item.months.map(month => month.label),
						formatLabel: ({ label }) => {
							const [year, month] = label.split("/");
							const date = new Date(Number(year), Number(month) - 1, 1);
							return formatDate(date, { month: "long", year: "numeric" });
						},
						customSort: (a, b) => a?.value?.localeCompare(b?.value),
					},
				],
			},

			{
				facetId: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
				facetTitle: formatMessage({ id: "product.page.filter.property.name.label" }),
				filters: [
					{
						field: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
						title: formatMessage({ id: "product.page.filter.property.name.label" }),
						allowSearch: true,
						search: { placeholderKey: "filter.property.name.searchbar.placeholder" },
						valueGetter: product => product.id,
						buildOptions: products =>
							products
								.filter(
									product =>
										!Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(
											product?.productExperience
										)
								)
								.map(product => ({ value: product.id, label: product.name }))
								.sort((a, b) => a?.label?.localeCompare(b?.label)),
						optionMode: OPTION_MODE.MANUAL_ONLY,
						showCount: false,
					},
				],
			},
		],
		[shouldShowMultiStageJourneyOption]
	);

	const handleFilterOpen = (filterId, isSticky) => {
		sendTagOnProductsFilterOpen({
			filterId,
			...(!isMobile &&
				typeof isSticky === "boolean" && { filterPosition: isSticky ? "sticky" : "top" }),
		});
	};

	const handleMobileFilterOpen = isSticky => {
		track("listingPage_filters_mobile_open", {
			filterPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleSortOpen = isSticky => {
		track("user_click_sort-button", {
			sortPosition: isSticky ? "sticky" : "top",
		});
	};

	const handleFilterChange = (facetName, facetValue) => {
		track("pp-filter-facet-clicked", { facetName, facetValue });
	};

	const handleSortChange = sortById => {
		track("sort_product_list", { sortby: sortById });
	};

	const handleScrollToTop = () => {
		if (!topOfProductListRef.current) {
			return;
		}

		requestAnimationFrame(() => {
			topOfProductListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	};

	return (
		<FilterProvider facetConfigs={facetConfigs} products={products}>
			<SortProvider comparators={CUSTOM_COMPARATORS}>
				<div className="listing">
					<ListingHeadMetadata />
					<ListingHeaderContainer
						idSpotlight={idSpotlight}
						scrollToListingHeader={scrollToListingHeader}
						listingHeaderRef={listingHeaderRef}
						displayProductsCount={displayProductsCount}
					/>

					<div ref={topOfProductListRef} aria-hidden="true" />

					<ActionBar
						showMiniSearch={false}
						showMapButton={false}
						isLoading={false}
						onFilterOpen={handleFilterOpen}
						onFilterTriggerMobileOpen={handleMobileFilterOpen}
						onSortOpen={handleSortOpen}
						onFilterChange={handleFilterChange}
						onSubmit={handleScrollToTop}
						onSortChange={handleSortChange}
					/>

					<ListingContentContainer
						displayProductsCount={displayProductsCount}
						spotlightUri={idSpotlight}
					/>

					<PartnerSEO
						title={listingSEO.title}
						description={listingSEO.description}
						resolution={resolution}
					/>

					<ScrollToTopButton />
				</div>
				<PushNotificationContainer />
			</SortProvider>
		</FilterProvider>
	);
};

Listing.propTypes = {
	idSpotlight: PropTypes.string,
	listingSEO: PropTypes.object,
	displayProductsCount: PropTypes.bool,
	resolution: PropTypes.string,
	products: PropTypes.arrayOf(PropTypes.object),
};

export default Listing;
